import {
  useCombobox,
  Combobox,
  InputBase,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { ChevronDown } from 'react-feather';
import TagManager from 'react-gtm-module';
import { Icon } from '@/components/Icon/Icon';
import { SelectItem } from '@/components';
import { GoogleEvents } from '@/features/googleEvents';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import classes from './ReleaseSelector.module.css';
import type { ReleaseOption } from './ReleaseSelector';

type Props = {
  options: ReleaseOption[];
  selectedId: string;
  onChange: (id: string) => void;
  iconSize?: number;
  mode?: 'shadowed' | 'light';
  customClassName?: string;
  showAnalysisDateLabel?: boolean;
};

export const ReleaseSelectorDesktop = ({
  options,
  selectedId,
  onChange,
  iconSize = 16,
  mode = 'shadowed',
  customClassName,
  showAnalysisDateLabel = false,
}: Props) => {
  const theme = useMantineTheme();
  const intl = useClientTerminology();
  const combobox = useCombobox();
  const selectedLabel =
    options.find((o) => o.value === selectedId)?.label ?? '';

  return (
    <div
      className={`${customClassName ?? ''} ${classes[mode]}`}
      data-testid="release-selector"
    >
      <Combobox
        store={combobox}
        classNames={classes}
        data-testid="release-selector-desktop"
        width="max-content"
        onOptionSubmit={(id: string) => {
          onChange(id);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <InputBase
            classNames={classes}
            component="button"
            type="button"
            pointer
            data-testid="release-selector-button"
            onClick={() => {
              combobox.toggleDropdown();
              if (!combobox.dropdownOpened) {
                TagManager.dataLayer({
                  dataLayer: { event: GoogleEvents.releaseSelectorOpened },
                });
              }
            }}
            leftSectionWidth={44}
            rightSectionWidth={44}
            leftSection={<Icon name="Historical" size={iconSize} />}
            rightSection={
              <Icon
                Component={ChevronDown}
                color={theme.other.mainColor.foreground.primary}
              />
            }
            multiline
          >
            <Text className={classes.selectedOption}>
              {showAnalysisDateLabel &&
                intl.formatMessage({ id: 'filter.analysisDateTitle' }) + ': '}
              {selectedLabel}
            </Text>
          </InputBase>
        </Combobox.Target>
        <Combobox.Dropdown>
          <Combobox.Options>
            {options.map((release) => (
              <Combobox.Option value={release.value} key={release.value}>
                <SelectItem
                  label={release.label}
                  selected={selectedId === release.value}
                  value={release.value}
                  textStyles={{
                    fontWeight: theme.other.typography.weight.medium,
                    textAlign: 'left',
                  }}
                />
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
};
